<template>
  <b-container class="py-3 contract-view">
    <h4 class="mb-3"><b-icon-file-earmark-text /> Gerar contrato</h4>

    <!-- 1. Negócio -->
    <b-card class="mb-3" title="1. Negócio">
      <v-select
        v-model="deal"
        label="name"
        :filterable="false"
        :options="dealOpts"
        placeholder="Procure o negócio pelo número ou nome..."
        @search="searchDeals"
        @input="loadDeal"
      >
        <template slot="no-options">Digite para procurar...</template>
      </v-select>
      <div v-if="contracts.length" class="mt-2 small">
        Contratos já gerados:
        <span v-for="c in contracts" :key="c.id" class="mr-2">
          <a :href="c.google_doc_url" target="_blank">{{ c.numero }}</a>
          ({{ new Date(c.created_at).toLocaleDateString('pt-BR') }}
          <b-link @click="reuse(c.id)">reusar dados</b-link>)
        </span>
      </div>
    </b-card>

    <!-- 2. Texto -> IA -->
    <b-card class="mb-3" title="2. Orçamento / briefing (opcional)">
      <b-form-textarea
        v-model="text"
        rows="6"
        placeholder="Cole aqui o orçamento, pedido ou briefing. A IA preenche os campos abaixo e você revisa."
      />
      <b-button
        class="mt-2"
        variant="primary"
        :disabled="!text || extracting"
        @click="extract"
      >
        <b-spinner v-if="extracting" small /> Preencher campos com IA
      </b-button>
    </b-card>

    <!-- 3. Campos -->
    <b-card class="mb-3" title="3. Revise os dados">
      <b-form-row>
        <b-col md="3"><b-form-group label="Nº contrato"><b-form-input v-model="f.numero" /></b-form-group></b-col>
        <b-col md="3"><b-form-group label="Nº pedido"><b-form-input v-model="f.pedido_num" /></b-form-group></b-col>
        <b-col md="6"><b-form-group label="Data por extenso"><b-form-input v-model="f.data_extenso" /></b-form-group></b-col>
      </b-form-row>
      <b-form-row>
        <b-col md="8"><b-form-group label="Comprador"><b-form-input v-model="f.comprador_nome" /></b-form-group></b-col>
        <b-col md="4"><b-form-group label="CPF / CNPJ"><b-form-input v-model="f.comprador_cpf" /></b-form-group></b-col>
      </b-form-row>
      <b-form-group label="Endereço do comprador / obra"><b-form-input v-model="f.comprador_endereco" /></b-form-group>
      <b-form-group label="Qualificação" description="Ex.: brasileiro, casado, engenheiro — ou 'pessoa jurídica de direito privado, representada por ...'">
        <b-form-input v-model="f.comprador_qualificacao" />
      </b-form-group>

      <b-card v-if="f.vendedora" no-body class="mb-3">
        <b-card-header class="d-flex align-items-center py-2">
          <b-link v-b-toggle.vendedora class="font-weight-bold">Vendedora</b-link>
          <span class="ml-2 small text-muted">{{ f.vendedora.razao_social }}</span>
          <b-badge v-if="vendedoraAlterada" variant="warning" class="ml-2">alterada</b-badge>
          <b-button
            v-if="vendedoraAlterada"
            size="sm"
            variant="outline-secondary"
            class="ml-auto"
            @click="resetVendedora"
            >Restaurar padrão</b-button
          >
        </b-card-header>
        <b-collapse id="vendedora">
          <b-card-body>
            <b-form-row>
              <b-col md="8"><b-form-group label="Razão social"><b-form-input v-model="f.vendedora.razao_social" /></b-form-group></b-col>
              <b-col md="4"><b-form-group label="CNPJ"><b-form-input v-model="f.vendedora.cnpj" /></b-form-group></b-col>
            </b-form-row>
            <b-form-group label="Endereço (sede / fábrica)"><b-form-input v-model="f.vendedora.endereco" /></b-form-group>
            <b-form-row>
              <b-col md="5"><b-form-group label="Representante legal"><b-form-input v-model="f.vendedora.representante" /></b-form-group></b-col>
              <b-col md="3"><b-form-group label="CPF do representante"><b-form-input v-model="f.vendedora.representante_cpf" /></b-form-group></b-col>
              <b-col md="4"><b-form-group label="Chave PIX"><b-form-input v-model="f.vendedora.pix_chave" /></b-form-group></b-col>
            </b-form-row>
          </b-card-body>
        </b-collapse>
      </b-card>

      <b-form-checkbox v-model="f.material_por_conta_do_cliente" class="mb-2">
        Material por conta do cliente (contrato só de mão de obra)
      </b-form-checkbox>
      <b-form-group v-if="f.material_por_conta_do_cliente" label="Endereço de entrega do material pelo cliente">
        <b-form-input v-model="f.endereco_entrega_material_cliente" placeholder="Padrão: fábrica Decorea" />
      </b-form-group>

      <h6 class="mt-3">Ambientes</h6>
      <b-form-row v-for="(a, i) in f.ambientes" :key="'a' + i" class="mb-2 align-items-end">
        <b-col md="3"><b-form-input v-model="a.nome" placeholder="Ambiente (ex.: Cozinha)" /></b-col>
        <b-col md="3"><b-form-input v-model="a.material" placeholder="Material" /></b-col>
        <b-col md="4"><b-form-input v-model="a.pecas_resumo" placeholder="Peças (ex.: tampos, saias e sóculos)" /></b-col>
        <b-col md="1"><b-form-input v-model="a.valor" placeholder="R$" /></b-col>
        <b-col md="1"><b-button variant="outline-danger" size="sm" @click="f.ambientes.splice(i, 1)">×</b-button></b-col>
      </b-form-row>
      <b-button size="sm" variant="outline-secondary" @click="f.ambientes.push({})">+ ambiente</b-button>

      <b-form-row class="mt-3">
        <b-col md="4"><b-form-group label="Valor total (R$)"><b-form-input v-model="f.valor_total" /></b-form-group></b-col>
        <b-col md="4"><b-form-group label="Área total sem perda (m²)"><b-form-input v-model="f.area_total_sem_perda" /></b-form-group></b-col>
        <b-col md="4"><b-form-group label="Área total com perda (m²)"><b-form-input v-model="f.area_total_com_perda" /></b-form-group></b-col>
      </b-form-row>

      <h6>Parcelas</h6>
      <b-form-row v-for="(p, i) in f.parcelas" :key="'p' + i" class="mb-2">
        <b-col md="3"><b-form-input v-model="p.valor" placeholder="R$" /></b-col>
        <b-col md="8"><b-form-input v-model="p.descricao" placeholder="ex.: no fechamento do pedido" /></b-col>
        <b-col md="1"><b-button variant="outline-danger" size="sm" @click="f.parcelas.splice(i, 1)">×</b-button></b-col>
      </b-form-row>
      <b-button size="sm" variant="outline-secondary" @click="f.parcelas.push({})">+ parcela</b-button>

      <b-form-row class="mt-3">
        <b-col md="4"><b-form-group label="Meio de pagamento"><b-form-input v-model="f.meio_pagamento" /></b-form-group></b-col>
        <b-col md="4"><b-form-group label="Prazo (dias úteis)"><b-form-input v-model="f.prazo_dias_uteis" type="number" /></b-form-group></b-col>
        <b-col md="4" class="d-flex align-items-center">
          <b-form-checkbox v-model="f.entrega_na_obra">Entrega na obra (senão retirada na fábrica)</b-form-checkbox>
        </b-col>
      </b-form-row>

      <b-form-group label="Anexos (um por linha)" description="Ex.: Pedido formalizado nº 26145, contendo a relação detalhada de peças...">
        <b-form-textarea v-model="anexosText" rows="2" />
      </b-form-group>
      <b-form-group label="Parágrafo único — ciência do comprador (opcional)">
        <b-form-textarea v-model="f.paragrafo_unico_ciencia" rows="3" />
      </b-form-group>

      <b-alert v-for="w in warnings" :key="w" show variant="warning" class="py-1 mb-1">{{ w }}</b-alert>
    </b-card>

    <!-- 4. Gerar -->
    <b-card class="mb-3" title="4. Gerar Google Doc">
      <b-form-checkbox v-model="useSignature" :disabled="!signatureAllowed">
        Incluir assinatura digitalizada do representante
        <span v-if="!signature.usable" class="text-muted small">
          (não autorizada por {{ signature.owner_emails.join(', ') }})
        </span>
        <span v-else-if="!signatureAllowed" class="text-muted small">
          (representante da vendedora foi alterado)
        </span>
      </b-form-checkbox>
      <b-button class="mt-2" variant="success" :disabled="generating" @click="generate">
        <b-spinner v-if="generating" small /> Gerar contrato no Google Docs
      </b-button>
      <b-alert v-if="docUrl" show variant="success" class="mt-2">
        Contrato criado. <a :href="docUrl" target="_blank">Abrir no Google Docs</a> — edite o que precisar e use
        Arquivo → Fazer download → PDF.
      </b-alert>
    </b-card>

    <!-- Assinatura: só o dono vê -->
    <b-card v-if="signature.can_manage" class="mb-3" title="Minha assinatura" border-variant="info">
      <p class="small text-muted">
        Somente você pode autorizar o uso da sua assinatura nos contratos gerados pelo CRM. Você pode revogar a
        qualquer momento.
      </p>
      <img v-if="signature.image" :src="signature.image" style="max-height: 80px" class="mb-2 d-block" />
      <b-form-file v-model="signatureFile" accept="image/png" placeholder="Enviar PNG da assinatura" class="mb-2" />
      <b-button variant="info" class="mr-2" :disabled="!signature.has_image && !signatureFile" @click="saveSignature(true)">
        Autorizar uso
      </b-button>
      <b-button v-if="signature.usable" variant="outline-danger" @click="saveSignature(false)">Revogar</b-button>
      <span v-if="signature.usable" class="small ml-2 text-success">
        Autorizada em {{ new Date(signature.authorized_at).toLocaleString('pt-BR') }}
      </span>
    </b-card>
  </b-container>
</template>

<script>
import debounce from 'lodash/debounce'

const emptyForm = () => ({
  numero: '',
  pedido_num: '',
  data_extenso: '',
  comprador_nome: '',
  comprador_cpf: '',
  comprador_endereco: '',
  comprador_qualificacao: 'brasileiro',
  vendedora: null,
  material_por_conta_do_cliente: false,
  endereco_entrega_material_cliente: '',
  ambientes: [{}],
  valor_total: '',
  area_total_sem_perda: '',
  area_total_com_perda: '',
  parcelas: [{}],
  meio_pagamento: 'PIX',
  prazo_dias_uteis: 20,
  entrega_na_obra: true,
  paragrafo_unico_ciencia: '',
  anexos: []
})

// "1.234,50" | "1234.5" | 1234.5 -> 1234.5
const toNum = (v) => {
  if (v === null || v === undefined || v === '') return 0
  if (typeof v === 'number') return v
  let s = String(v).replace(/[^\d,.-]/g, '')
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.')
  return parseFloat(s) || 0
}
const brl = (v) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export default {
  metaInfo: { title: 'Contratos' },
  data() {
    return {
      deal: null,
      dealOpts: [],
      contracts: [],
      text: '',
      extracting: false,
      generating: false,
      f: emptyForm(),
      anexosText: '',
      useSignature: false,
      signature: { owner_emails: [], usable: false, can_manage: false, has_image: false },
      signatureFile: null,
      vendedoraDefault: null,
      docUrl: null
    }
  },
  computed: {
    signatureAllowed() {
      return (
        this.signature.usable &&
        !!this.f.vendedora &&
        !!this.vendedoraDefault &&
        this.f.vendedora.representante === this.vendedoraDefault.representante
      )
    },
    vendedoraAlterada() {
      return (
        !!this.vendedoraDefault &&
        !!this.f.vendedora &&
        Object.keys(this.vendedoraDefault).some((k) => (this.f.vendedora[k] || '') !== this.vendedoraDefault[k])
      )
    },
    warnings() {
      const w = []
      const total = toNum(this.f.valor_total)
      const parcelas = this.f.parcelas.reduce((s, p) => s + toNum(p.valor), 0)
      if (total && parcelas && Math.abs(total - parcelas) > 0.01) {
        w.push(`Soma das parcelas (${brl(parcelas)}) ≠ valor total (${brl(total)}).`)
      }
      return w
    }
  },
  watch: {
    signatureAllowed(ok) {
      if (!ok) this.useSignature = false
    }
  },
  created() {
    this.loadSignature()
    this.api('/api/contracts/defaults')
      .then((d) => {
        this.vendedoraDefault = d.vendedora
        if (!this.f.vendedora) this.resetVendedora()
      })
      .catch(this.fail)
    if (this.$route.query.deal) this.loadDeal({ id: this.$route.query.deal })
  },
  methods: {
    api(url, { method = 'GET', body, form } = {}) {
      const headers = { Accept: 'application/json' }
      const token = document.querySelector('meta[name="csrf-token"]')
      if (token) headers['X-CSRF-Token'] = token.content
      if (body) headers['Content-Type'] = 'application/json'
      return fetch(url, {
        method,
        headers,
        credentials: 'same-origin',
        body: form || (body && JSON.stringify(body))
      }).then(async (r) => {
        const data = await r.json().catch(() => ({}))
        if (!r.ok) throw new Error(data.error || `Erro ${r.status}`)
        return data
      })
    },
    fail(e) {
      this.$root.toast('Erro', e.message, 'danger')
    },

    searchDeals(search, loading) {
      if (search.length < 2) return
      loading(true)
      this.doSearch(search, loading, this)
    },
    doSearch: debounce((search, loading, vm) => {
      vm.api(`/api/users/search?query=${encodeURIComponent(search)}`)
        .then((data) => {
          vm.dealOpts = data.filter((o) => 'stage' in o)
        })
        .catch(vm.fail)
        .finally(() => loading(false))
    }, 350),

    loadDeal(deal) {
      if (!deal) return
      this.api(`/api/contracts/prefill?deal_id=${deal.id}`)
        .then((data) => {
          this.deal = { id: data.deal.id, name: data.deal.title }
          this.merge(data.fields)
        })
        .catch(this.fail)
      this.api(`/api/contracts?deal_id=${deal.id}`)
        .then((data) => (this.contracts = data))
        .catch(this.fail)
    },

    resetVendedora() {
      this.f.vendedora = { ...this.vendedoraDefault }
    },

    reuse(id) {
      this.api(`/api/contracts/${id}`)
        .then((c) => this.merge(c.data, true))
        .catch(this.fail)
    },

    // Copies non-empty values over the form; overwrite=true replaces everything.
    merge(fields, overwrite = false) {
      Object.keys(fields || {}).forEach((k) => {
        const v = fields[k]
        const empty = v === null || v === '' || (Array.isArray(v) && v.length === 0)
        if (!overwrite && empty) return
        const copy = v !== null && typeof v === 'object' ? JSON.parse(JSON.stringify(v)) : v
        this.$set(this.f, k, copy)
      })
      if (fields && fields.anexos && fields.anexos.length) this.anexosText = fields.anexos.join('\n')
      if (!this.f.ambientes.length) this.f.ambientes.push({})
      if (!this.f.parcelas.length) this.f.parcelas.push({})
    },

    extract() {
      this.extracting = true
      this.api('/api/contracts/extract', { method: 'POST', body: { text: this.text } })
        .then((data) => {
          this.merge(data.fields)
          const errors = (data.check && data.check.errors) || []
          if (errors.length) this.$root.toast('Revise', errors.join(', '), 'warning')
        })
        .catch(this.fail)
        .finally(() => (this.extracting = false))
    },

    generate() {
      this.generating = true
      this.docUrl = null
      const contract = { ...this.f, anexos: this.anexosText.split('\n').map((s) => s.trim()).filter(Boolean) }
      this.api('/api/contracts', {
        method: 'POST',
        body: { deal_id: this.deal && this.deal.id, use_signature: this.useSignature, contract }
      })
        .then((data) => {
          this.docUrl = data.url
          window.open(data.url, '_blank')
          if (this.deal) this.loadDeal(this.deal)
        })
        .catch(this.fail)
        .finally(() => (this.generating = false))
    },

    loadSignature() {
      this.api('/api/contracts/signature')
        .then((s) => {
          this.signature = s
          if (!s.usable) this.useSignature = false
        })
        .catch(this.fail)
    },
    saveSignature(authorized) {
      const form = new FormData()
      form.append('authorized', authorized)
      if (this.signatureFile) form.append('image', this.signatureFile)
      this.api('/api/contracts/signature', { method: 'PUT', form })
        .then((s) => {
          this.signature = s
          this.signatureFile = null
        })
        .catch(this.fail)
    }
  }
}
</script>
